<template>
  <section class="">
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="[
        paddingTop === 'reduced' ? 'pt-6 lg:pt-6' : 'pt-12 lg:pt-16',
        paddingBottom === 'reduced' ? 'pb-6 lg:pb-6' : 'pb-12 lg:pb-16'
      ]"
    >
      <div class="max-w-3xl">
        <h2
          v-if="title"
          class="text-center text-34 leading-42 font-ww-bold text-[#3B2314]"
          v-html="title"
        />

        <div
          class="flex flex-col gap-4"
          :class="title ? 'pt-3' : ''"
        >
          <div
            v-for="(item, index) in items"
            :key="index"
            class="overflow-hidden rounded-xl border border-[#E0D5C5] bg-white"
          >
            <button
              :id="`faq-${index}-btn`"
              type="button"
              class="flex gap-6 items-start px-6 pt-5 w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F1574B]"
              :class="isOpen(index) ? 'pb-0' : 'pb-5'"
              :aria-expanded="isOpen(index)"
              :aria-controls="`faq-${index}-panel`"
              @click="toggle(index)"
            >
              <span
                class="flex-1 min-w-0 text-19 leading-6 lg:leading-32 font-ww-bold text-[#4A4A4A]"
                v-html="item.q"
              />

              <span
                class="inline-flex shrink-0 justify-center items-center rounded-2xl border border-[#E0D5C5] size-8"
                aria-hidden="true"
              >
                <span
                  class="text-xs leading-5 text-[#E8442A] font-ww-bold transition-transform duration-200 ease-out"
                  :class="isOpen(index) ? 'rotate-0' : 'rotate-180'"
                >▼</span>
              </span>
            </button>

            <transition
              enter-active-class="transition-all duration-300 ease-out"
              leave-active-class="transition-all duration-300 ease-in"
              @enter="onEnter"
              @after-enter="onAfterEnter"
              @leave="onLeave"
            >
              <div
                v-show="isOpen(index)"
                :id="`faq-${index}-panel`"
                role="region"
                :aria-labelledby="`faq-${index}-btn`"
                class="px-6 pt-4 pb-5 md:text-17 text-base leading-6 text-[#4A4A4A] lg:mr-16 font-ww-regular [&_p]:m-0 [&_p.faq-gap]:h-5 [&_p.faq-gap]:leading-none [&_sup]:text-xs"
                v-html="item.a"
              />
            </transition>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const faqItemsDefault = [
  {
    q: 'How much nitric oxide is recommended for daily intake?',
    a: `<p>Nitric oxide supplements do not contain nitric oxide. Instead, they are made up of the building blocks your body needs to increase nitric oxide production.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Most find the best nitric oxide supplements contain at least L-arginine and L-citrulline, and the typical recommendations regarding dosage are:</p>
<p>L-arginine - 1-3 grams per day</p>
<p>L-citrulline - 2.4-6 grams per day</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>You may also find that nitrates and a variety of antioxidant sources are common components of nitric oxide supplements.</p>`
  },
  {
    q: 'How much nattokinase is recommended for daily intake?',
    a: `<p>For maintenance and cardiovascular support, most recommend 2,000 FU of nattokinase per day.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>The recommendations for nattokinase intake while treating a cardiovascular condition, such as atherosclerosis or hyperlipidemia exceed the above recommendation (between 6000-12000 FU daily), however, you may wish to consult your physician prior to implementing a higher dose.</p>`
  },
  {
    q: 'To reap the benefits of walking, how fast does my pace need to be?',
    a: `<p>Any movement is great, but to reap the greatest rewards of walking, a brisk pace is typically recommended.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>So, while you certainly do not need to sprint, you do want to get your heart pumping.</p>`
  },
  {
    q: 'What is the best form of Omega-3 to take as a supplement?',
    a: `<p>Fish or algal oil is a great source of omega-3 fatty acids, and these sources typically contain the highest amounts of EPA and DHA.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Generally, omega-3 supplement doses of less than 5000 mg per day are recommended, though this advice can vary depending on condition. Ideal is 600mg of EPA/DHA combined.</p>`
  },
  {
    q: 'Do word and number puzzles as well as video games really improve brain health?',
    a: `<p>Yes!</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Studies show that activities such as puzzles, number games, and even video games stimulate your brain's ability to not only adapt but also create new connections.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Over time, this improves your ability to focus, remember, and solve problems.</p>`
  },
  {
    q: 'What should I look for in a probiotic?',
    a: `<p>Probiotic usage is often guided by your individual needs.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>For men, the typical recommendation hovers around 10-20 billion CFUs taken daily (consistency is key).</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Kombucha, kimchi, kefir, miso, and sauerkraut are also great natural food sources rich in probiotics.</p>`
  },
  {
    q: 'How much sodium butyrate is recommended daily?',
    a: `<p>Sodium butyrate dosage recommendations often vary by need.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>For general supplementation, 150-300 mg is recommended, however, this amount is commonly advised to be taken in smaller doses throughout the day with meals.</p>`
  },
  {
    q: 'Should I only take creatine on the days I do strength or resistance training?',
    a: `<p>You can actually take creatine daily. And, the recommended dosage is 3-5 grams per day.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Most experts warn against taking more than this as it can put strain on your kidneys.</p>`
  },
  {
    q: 'What are the benefits of coffee, per cup, for energy, cardiovascular health, and brain health?',
    a: `<p>Drinking 1-2 cups of coffee daily is linked to a decreased risk of heart failure.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Studies show that drinking 2-3 cups of coffee daily is associated with a lower risk of heart disease, dangerous heart rhythms, and may help you live longer.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Consuming 2 cups of coffee per day has been shown to reduce the risk of Alzheimer's disease, and drinking 2-3 cups daily may reduce the risk of dementia.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Moderate coffee consumption (anywhere between 2-5 cups daily) has been shown to slow cognitive decline.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>And, for "sustained energy" the FDA recommends consuming 3-4 cups of coffee daily, though you'll want to consider backing off on caffeine consumption in the afternoon to avoid disrupting sleep.</p>`
  }
]
</script>

<script setup>
const openIndex = ref(null)

defineProps({
  title: {
    type: String,
    default: 'FAQ'
  },
  items: {
    type: Array,
    default: () => faqItemsDefault
  },
  paddingTop: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingBottom: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

const isOpen = (index) => openIndex.value === index

const toggle = (index) => {
  openIndex.value = openIndex.value === index ? null : index
}

function onEnter(el) {
  el.style.height = '0px'
  el.style.overflow = 'hidden'
  el.style.opacity = '0'
  el.style.transition = 'height 300ms ease-out, opacity 250ms ease-out'

  requestAnimationFrame(() => {
    el.style.height = `${el.scrollHeight}px`
    el.style.opacity = '1'
  })
}

function onAfterEnter(el) {
  el.style.height = 'auto'
  el.style.overflow = 'visible'
  el.style.opacity = '1'
}

function onLeave(el) {
  el.style.height = `${el.scrollHeight}px`
  el.style.overflow = 'hidden'
  el.style.opacity = '1'
  el.style.transition = 'height 300ms ease-in, opacity 200ms ease-in'

  requestAnimationFrame(() => {
    el.style.height = '0px'
    el.style.opacity = '0'
  })
}

defineBuilderComponent({
  friendlyName: 'Content - FAQ Accordion',
  inputs: [
    {
      name: 'title',
      type: 'text',
      defaultValue: 'FAQ'
    },
    {
      name: 'items',
      type: 'list',
      defaultValue: [
        {
          q: 'How much nitric oxide is recommended for daily intake?',
          a: `<p>Nitric oxide supplements do not contain nitric oxide. Instead, they are made up of the building blocks your body needs to increase nitric oxide production.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Most find the best nitric oxide supplements contain at least L-arginine and L-citrulline, and the typical recommendations regarding dosage are:</p>
<p>L-arginine - 1-3 grams per day</p>
<p>L-citrulline - 2.4-6 grams per day</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>You may also find that nitrates and a variety of antioxidant sources are common components of nitric oxide supplements.</p>`
        },
        {
          q: 'How much nattokinase is recommended for daily intake?',
          a: `<p>For maintenance and cardiovascular support, most recommend 2,000 FU of nattokinase per day.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>The recommendations for nattokinase intake while treating a cardiovascular condition, such as atherosclerosis or hyperlipidemia exceed the above recommendation (between 6000-12000 FU daily), however, you may wish to consult your physician prior to implementing a higher dose.</p>`
        },
        {
          q: 'To reap the benefits of walking, how fast does my pace need to be?',
          a: `<p>Any movement is great, but to reap the greatest rewards of walking, a brisk pace is typically recommended.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>So, while you certainly do not need to sprint, you do want to get your heart pumping.</p>`
        },
        {
          q: 'What is the best form of Omega-3 to take as a supplement?',
          a: `<p>Fish or algal oil is a great source of omega-3 fatty acids, and these sources typically contain the highest amounts of EPA and DHA.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Generally, omega-3 supplement doses of less than 5000 mg per day are recommended, though this advice can vary depending on condition. Ideal is 600mg of EPA/DHA combined.</p>`
        },
        {
          q: 'Do word and number puzzles as well as video games really improve brain health?',
          a: `<p>Yes!</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Studies show that activities such as puzzles, number games, and even video games stimulate your brain's ability to not only adapt but also create new connections.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Over time, this improves your ability to focus, remember, and solve problems.</p>`
        },
        {
          q: 'What should I look for in a probiotic?',
          a: `<p>Probiotic usage is often guided by your individual needs.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>For men, the typical recommendation hovers around 10-20 billion CFUs taken daily (consistency is key).</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Kombucha, kimchi, kefir, miso, and sauerkraut are also great natural food sources rich in probiotics.</p>`
        },
        {
          q: 'How much sodium butyrate is recommended daily?',
          a: `<p>Sodium butyrate dosage recommendations often vary by need.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>For general supplementation, 150-300 mg is recommended, however, this amount is commonly advised to be taken in smaller doses throughout the day with meals.</p>`
        },
        {
          q: 'Should I only take creatine on the days I do strength or resistance training?',
          a: `<p>You can actually take creatine daily. And, the recommended dosage is 3-5 grams per day.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Most experts warn against taking more than this as it can put strain on your kidneys.</p>`
        },
        {
          q: 'What are the benefits of coffee, per cup, for energy, cardiovascular health, and brain health?',
          a: `<p>Drinking 1-2 cups of coffee daily is linked to a decreased risk of heart failure.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Studies show that drinking 2-3 cups of coffee daily is associated with a lower risk of heart disease, dangerous heart rhythms, and may help you live longer.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Consuming 2 cups of coffee per day has been shown to reduce the risk of Alzheimer's disease, and drinking 2-3 cups daily may reduce the risk of dementia.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>Moderate coffee consumption (anywhere between 2-5 cups daily) has been shown to slow cognitive decline.</p>
<p class="faq-gap" aria-hidden="true"></p>
<p>And, for "sustained energy" the FDA recommends consuming 3-4 cups of coffee daily, though you'll want to consider backing off on caffeine consumption in the afternoon to avoid disrupting sleep.</p>`
        }
      ],
      subFields: [
        {
          name: 'q',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'a',
          type: 'richText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>
